import { useCallback, useEffect } from 'react';
import { VideoManagementIframe } from './VideoManagementIframe';
import { COMMON_ACTIONS, getDetailsConfig } from '../state/videoPanel';
import { VideoPanelState } from '../state/videoPanel';
import { useRegisterHostNavigate } from '../messaging/hooks/useHostNavigate';
import { useRegisterBulkInsertVideos, useRegisterCloseVideoPanel, useRegisterInsertVideo, useRegisterVideoDeleted } from '../messaging/hooks/videoManagement';
import { useSetupMagpie } from '../messaging/hooks/useSetupMagpie';
import { ClipEditorIframe } from './ClipEditorIframe';
import { PanelTypes } from '../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useVideoPanelFrame = () => {
  useSetupMagpie('video-components-external');
  VideoPanelState.hooks.useSyncState();
  useRegisterHostNavigate();
};
const useInitializePanelState = ({
  panelType,
  pickerPanelOptions,
  detailsPanelOptions,
  enableAdvancedFeaturesOnInsert,
  enablePrivateInsert
}) => {
  const {
    allowBulkSelect,
    bulkSelectionLimit,
    allowUpload
  } = pickerPanelOptions || {};
  const {
    fileId,
    selectedTab,
    showDelete,
    showShare
  } = detailsPanelOptions || {};
  useEffect(() => {
    VideoPanelState.setState(draft => {
      // Sync activePanel with panelType prop
      draft.activePanel = panelType !== null && panelType !== void 0 ? panelType : null;
      draft.enableAdvancedFeaturesOnInsert = enableAdvancedFeaturesOnInsert;
      draft.enablePrivateInsert = enablePrivateInsert !== null && enablePrivateInsert !== void 0 ? enablePrivateInsert : false;
      if (panelType === PanelTypes.PICKER) {
        draft.isManagementPanelLoading = true;
        draft.state.PICKER.allowUpload = allowUpload !== null && allowUpload !== void 0 ? allowUpload : true;
        draft.state.PICKER.allowBulkSelect = allowBulkSelect !== null && allowBulkSelect !== void 0 ? allowBulkSelect : false;
        if (bulkSelectionLimit) {
          draft.state.PICKER.bulkSelectionLimit = bulkSelectionLimit;
        }
        draft.config.DETAILS = {
          panelActions: {
            primary: {
              type: 'INSERT'
            },
            secondary: {
              type: 'EDIT'
            },
            tertiary: COMMON_ACTIONS
          }
        };
      }
      if (panelType === PanelTypes.DETAILS) {
        draft.isManagementPanelLoading = true;
        draft.state.DETAILS.fileId = fileId;
        draft.config.DETAILS = getDetailsConfig({
          showDelete,
          showShare
        });
        if (selectedTab) {
          draft.state.DETAILS.selectedTab = selectedTab;
        }
      }
    });
  }, [panelType, fileId, selectedTab, showDelete, showShare, allowUpload, enableAdvancedFeaturesOnInsert, allowBulkSelect, bulkSelectionLimit, enablePrivateInsert]);
};
export const VideoPanel = ({
  panelType,
  detailsPanelOptions,
  pickerPanelOptions,
  onSelect,
  onBulkSelect,
  onDeleteVideo,
  onClose,
  shouldEncodeToHlsOnInsert = true,
  enablePrivateInsert = false,
  rootNode
}) => {
  const panelState = VideoPanelState.hooks.useState();
  useVideoPanelFrame();
  useInitializePanelState({
    panelType,
    detailsPanelOptions,
    pickerPanelOptions,
    enableAdvancedFeaturesOnInsert: shouldEncodeToHlsOnInsert,
    enablePrivateInsert
  });
  const handleClose = useCallback(() => {
    VideoPanelState.setState(draft => {
      draft.activePanel = null;
    });
    onClose === null || onClose === void 0 || onClose();
  }, [onClose]);
  const handleInsert = useCallback(video => {
    onSelect === null || onSelect === void 0 || onSelect(video);
    handleClose();
  }, [onSelect, handleClose]);
  const handleBulkInsert = useCallback(videos => {
    onBulkSelect === null || onBulkSelect === void 0 || onBulkSelect(videos);
    handleClose();
  }, [onBulkSelect, handleClose]);
  const handleDeleteVideo = useCallback((sourceFileId, videoTitle) => {
    onDeleteVideo === null || onDeleteVideo === void 0 || onDeleteVideo(sourceFileId, videoTitle);
    handleClose();
  }, [onDeleteVideo, handleClose]);
  useRegisterBulkInsertVideos({
    onBulkInsert: handleBulkInsert
  });
  useRegisterInsertVideo({
    onInsert: handleInsert
  });
  useRegisterVideoDeleted({
    onVideoDeleted: handleDeleteVideo
  });
  useRegisterCloseVideoPanel({
    onClose: handleClose
  });
  return /*#__PURE__*/_jsxs("div", {
    style: {
      display: panelState.activePanel ? 'block' : 'none'
    },
    children: [/*#__PURE__*/_jsx(VideoManagementIframe, {
      rootNode: rootNode,
      isActive: panelState.activePanel === PanelTypes.PICKER || panelState.activePanel === PanelTypes.DETAILS,
      isLoading: panelState.isManagementPanelLoading
    }), /*#__PURE__*/_jsx(ClipEditorIframe, {
      isActive: panelState.activePanel === PanelTypes.CLIP_EDITOR,
      projectId: panelState.state.CLIP_EDITOR.projectId,
      isLoading: panelState.state.CLIP_EDITOR.isLoading
    })]
  });
};